import { Box, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { color } from '@/colors';
import { AppPaper, Flex, FlexCenHorVer } from '@/UI/styles';
import { EditableLabel } from '@/components/Recipe/FlowComponents/Editor/Designer/LayerPanel/EditableLabel';
import { useDebugContext } from '@/hooks/useDebugContext';
import { useUserWorkflowRole, useWorkflowStore } from '@/state/workflow.state';

interface WorkflowDesignAppSwitchProps {
  isPaperBg: boolean;
  hideDevLabel?: boolean;
  goToDashboard?: () => void;
  isEditingName: boolean;
  setIsEditingName: (isEditingName: boolean) => void;
}

export const WorkflowDesignAppSwitch = ({
  isPaperBg = true,
  hideDevLabel = false,
  goToDashboard,
  isEditingName,
  setIsEditingName,
}: WorkflowDesignAppSwitchProps) => {
  const recipeName = useWorkflowStore((state) => state.recipe.name);
  const renameRecipe = useWorkflowStore((state) => state.renameRecipe);

  const [displayName, setDisplayName] = useState(recipeName);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const editorLabelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayName(recipeName);
  }, [recipeName]);

  useEffect(() => {
    if (isEditingName) {
      setIsHovering(false);
    }
  }, [isEditingName]);

  useEffect(() => {
    const el = editorLabelRef.current;
    if (!el || isEditingName) {
      setIsOverflowing(false);
      return;
    }
    const checkOverflow = () => {
      const contentEl = el.firstElementChild;
      if (contentEl) {
        setIsOverflowing(contentEl.scrollWidth > contentEl.clientWidth);
      }
    };
    const rafId = requestAnimationFrame(checkOverflow);
    let resizeObserver: ResizeObserver | undefined;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(el);
    }
    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [displayName, isEditingName]);

  const role = useUserWorkflowRole();
  const { DEBUG_TOGGLE } = useDebugContext();

  const isShowLogo = role === 'guest';

  const handleRenameSubmit = useCallback(
    async (newName: string) => {
      const trimmed = newName.trim();
      if (trimmed && trimmed !== recipeName) {
        setDisplayName(trimmed);
        try {
          await renameRecipe(trimmed);
        } catch {
          setDisplayName(recipeName);
        }
      }
    },
    [renameRecipe, recipeName],
  );

  const Container = isPaperBg ? AppPaper : FlexCenHorVer;

  return (
    <Container
      sx={{
        ...(isPaperBg ? { display: 'flex', alignItems: 'center' } : {}),
        width: '220px',
        height: '40px',
        position: 'relative',
        py: '6px',
        pl: isShowLogo ? 1.5 : 1,
        pr: 1,
      }}
    >
      <img
        src="/icons/logo.svg"
        alt="logo"
        width={24}
        height={24}
        onClick={() => goToDashboard?.()}
        style={{ display: isShowLogo ? 'block' : 'none', cursor: 'pointer', marginRight: '24px' }}
      />

      <Flex sx={{ flex: 1, minWidth: 0 }}>
        {role === 'editor' ? (
          <Tooltip
            title={displayName}
            open={!isEditingName && isOverflowing && isHovering}
            enterDelay={500}
            leaveDelay={0}
          >
            <Box
              ref={editorLabelRef}
              component="span"
              tabIndex={!isEditingName ? 0 : -1}
              sx={{ flex: 1, minWidth: 0, display: 'flex', outline: 'none' }}
              onMouseEnter={() => !isEditingName && setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <EditableLabel
                value={displayName}
                onSubmit={(newName) => void handleRenameSubmit(newName)}
                open={isEditingName}
                onClose={() => setIsEditingName(false)}
                onEditingChange={setIsEditingName}
                variant="body-std-md"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: 'max-content',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              />
            </Box>
          </Tooltip>
        ) : (
          <EllipsisText
            variant="body-std-md"
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
            }}
          >
            {displayName}
          </EllipsisText>
        )}
      </Flex>

      {!hideDevLabel && import.meta.env.MODE === 'development' ? (
        <Typography
          onClick={DEBUG_TOGGLE}
          sx={{
            position: 'absolute',
            right: -120,
            cursor: 'pointer',
            color: color.Dev_Label,
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          Development
        </Typography>
      ) : null}
    </Container>
  );
};
